import { AuditQuestion } from "../../models/AuditQuestion";
import { QuestionaryQuestion } from "../../models/QuestionaryQuestion";
import FileUploadService from "../../services/fileUpload.service";
import FileAuditUploadService from "../../services/fileAuditUpload.service";

const TEXT_BASED_TYPES = [1, 5, 7];
const CURRENCY_TYPE = 12;
const SELECTION_BASED_TYPES = [2, 3];
const MULTI_COLUMN_TYPE = 4;
const MATRIX_TYPE = 8;
const FILE_BASED_TYPES = [10, 11];
const NON_VALIDATABLE_TYPES = [6, 9, 13];

const getInvalidQuestionIds = async (
  questions: QuestionaryQuestion[],
  questionaryId: number
): Promise<number[]> => {
  const invalidIds: number[] = [];

  for (const question of questions) {
    if (
      !question.required ||
      NON_VALIDATABLE_TYPES.includes(question.questionTypeId)
    ) {
      continue;
    }

    let isAnswered = false;

    if (TEXT_BASED_TYPES.includes(question.questionTypeId)) {
      isAnswered = !!question.answerText && question.answerText.trim() !== "";
    } else if (question.questionTypeId === CURRENCY_TYPE) {
      isAnswered =
        !!question.answerText &&
        question.answerText.trim() !== "" &&
        parseFloat(question.answerText) !== 0;
    } else if (SELECTION_BASED_TYPES.includes(question.questionTypeId)) {
      isAnswered =
        question.questionaryAnswers?.some((answer) => answer.isChecked) ??
        false;
    } else if (question.questionTypeId === MULTI_COLUMN_TYPE) {
      try {
        const rows = JSON.parse(question.answerText || "[]");
        if (rows.length === 0) {
          isAnswered = false;
        } else {
          isAnswered = rows.some((row: object) =>
            Object.values(row).some((val) => (val as string).trim() !== "")
          );
        }
      } catch {
        isAnswered = false;
      }
    } else if (question.questionTypeId === MATRIX_TYPE) {
      try {
        const outerArray = JSON.parse(question.answerText || "[]");
        if (outerArray.length === 0) {
          isAnswered = false;
        } else {
          isAnswered = outerArray.some((rowString: string) => {
            const innerArray = JSON.parse(rowString);
            return innerArray.some((cell: string) => cell.trim() !== "");
          });
        }
      } catch {
        isAnswered = false;
      }
    } else if (FILE_BASED_TYPES.includes(question.questionTypeId)) {
      const files = await FileUploadService.getFilesAnswer(
        questionaryId,
        question.id
      );
      isAnswered = files.length > 0;
    }

    if (!isAnswered) {
      invalidIds.push(question.id);
    }
  }

  return invalidIds;
};

const getInvalidAuditQuestionIds = async (
  questions: AuditQuestion[],
  auditId: number
): Promise<number[]> => {
  const invalidIds: number[] = [];

  for (const question of questions) {
    if (
      !question.required ||
      NON_VALIDATABLE_TYPES.includes(question.questionTypeId)
    ) {
      continue;
    }

    let isAnswered = false;

    if (TEXT_BASED_TYPES.includes(question.questionTypeId)) {
      isAnswered = !!question.answerText && question.answerText.trim() !== "";
    } else if (question.questionTypeId === CURRENCY_TYPE) {
      isAnswered =
        !!question.answerText &&
        question.answerText.trim() !== "" &&
        parseFloat(question.answerText) !== 0;
    } else if (SELECTION_BASED_TYPES.includes(question.questionTypeId)) {
      isAnswered =
        question.auditAnswers?.some((answer) => answer.isChecked) ?? false;
    } else if (question.questionTypeId === MULTI_COLUMN_TYPE) {
      try {
        const rows = JSON.parse(question.answerText || "[]");
        if (rows.length === 0) {
          isAnswered = false;
        } else {
          isAnswered = rows.some((row: object) =>
            Object.values(row).some((val) => (val as string).trim() !== "")
          );
        }
      } catch {
        isAnswered = false;
      }
    } else if (question.questionTypeId === MATRIX_TYPE) {
      try {
        const outerArray = JSON.parse(question.answerText || "[]");
        if (outerArray.length === 0) {
          isAnswered = false;
        } else {
          isAnswered = outerArray.some((rowString: string) => {
            const innerArray = JSON.parse(rowString);
            return innerArray.some((cell: string) => cell.trim() !== "");
          });
        }
      } catch {
        isAnswered = false;
      }
    } else if (FILE_BASED_TYPES.includes(question.questionTypeId)) {
      const files = await FileAuditUploadService.getFilesAnswer(
        auditId,
        question.id
      );
      isAnswered = files.length > 0;
    }

    if (!isAnswered) {
      invalidIds.push(question.id);
    }
  }

  return invalidIds;
};

const validation = {
  getInvalidQuestionIds,
  getInvalidAuditQuestionIds,
};

export default validation;
